import type { PhysicalCategoryValue } from "../api/physicalRecordsCategoriesApi";

export function groupCategoryValues(values: PhysicalCategoryValue[]) {
  const groups = new Map<string, { label: string; rows: PhysicalCategoryValue[] }>();
  for (const value of values) {
    const key = JSON.stringify([value.objectCategoryId, value.legacyCategoryId, value.definitionVersion, value.objectVersion,
      value.regionName, value.setName, value.customId, value.parentKeyId, value.keyId, value.entryNum]);
    let group = groups.get(key);
    if (!group) {
      const parts = [value.regionName ?? "Unspecified region", value.setName ?? "No set",
        `Entry ${value.entryNum ?? "-"}`, `Parent ${value.parentKeyId ?? "-"}`, `Key ${value.keyId ?? "-"}`, `Custom ${value.customId ?? "-"}`];
      group = { label: parts.join(" / "), rows: [] };
      groups.set(key, group);
    }
    group.rows.push(value);
  }
  return Array.from(groups, ([key, group]) => ({ key, ...group }));
}

export function categoryValueFields(value: PhysicalCategoryValue): [string, string][] {
  const fields: [string, string | number | null][] = [
    ["Integer", value.valueInt], ["Real", value.valueReal], ["Date", value.valueDate],
    ["String", value.valueString], ["Long text", value.valueLong],
  ];
  return fields.filter((field): field is [string, string | number] => field[1] !== null)
    .map(([label, content]) => [label, content === "" ? '""' : String(content)]);
}

export function categoryReadError(error: unknown): { missingSchema: boolean; message: string } {
  const response = (error as { response?: { status?: number; data?: { code?: string; error?: string } } })?.response;
  const missingSchema = response?.status === 503 && response.data?.code === "physical_records_categories_schema_unavailable";
  if (missingSchema) return { missingSchema, message: response?.data?.error ?? "Physical Records category schema is unavailable. Contact an administrator." };
  if (response?.status === 403) return { missingSchema, message: "You do not have permission to browse these categories." };
  if (response?.status === 404) return { missingSchema, message: "This record or category is no longer available in your access scope." };
  return { missingSchema, message: response?.data?.error ?? "Unable to load categories. Please retry." };
}

export function presentCategoryValues(values: PhysicalCategoryValue[], categoryName: string) {
  const visible = values.filter((value) => !(value.legacyAttributeId === 1 && value.attributeName === null
    && value.valueString === categoryName && value.valueLong === null && value.valueInt === null
    && value.valueReal === null && value.valueDate === null));
  const groups = new Map<string, { label: string; rows: PhysicalCategoryValue[] }>();
  for (const value of visible) {
    const region = value.regionName && !/^Attr_\d+_\d+$/.test(value.regionName) ? value.regionName : null;
    const key = JSON.stringify([value.objectCategoryId, value.legacyCategoryId, value.definitionVersion,
      value.objectVersion, region, value.setName, value.customId, value.parentKeyId, value.keyId, value.entryNum]);
    let group = groups.get(key);
    if (!group) {
      const parts = [region, value.setName];
      if (value.entryNum !== null && value.entryNum !== 1) parts.push(`Entry ${value.entryNum}`);
      group = { label: parts.filter(Boolean).join(" / "), rows: [] };
      groups.set(key, group);
    }
    group.rows.push(value);
  }
  return Array.from(groups, ([key, group]) => ({ key, ...group,
    rows: group.rows.sort((first, second) => first.legacyAttributeId - second.legacyAttributeId
      || first.categoryValueId - second.categoryValueId) }));
}